/* global React, Icon, Card, Button, Stepper, toast, fmt */
// ============================================================
// PLUMME 3 — Privat økonomi · Skatt
// Designet fra Plumme-designsystemet: SuperPlumma-oppsummering,
// skattemeldingsassistent, AI Skattekontroll, Fradragsjeger,
// skatteoptimalisering og klar-for-innsending.
//
// Kontrollgrunnlaget bygges i backend fra privatøkonomi-readmodellen.
// Faktisk innsending er bevisst deaktivert til offisiell integrasjon,
// sterk autentisering og approval/audit finnes.
// ============================================================
const { useState: poTaxUseState, useMemo: poTaxUseMemo } = React;

// Delt seksjonsoverskrift for hele privatøkonomi-flaten.
function PoSecHead({ help, icon, right, sub, title, tm }) {
  const helpText = help || sub;
  return <div className="row between" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 14, marginBottom: 4 }}>
    <div className="aii-sec-head" style={{ marginBottom: 0 }}>
      <span className="aii-sec-ico"><Icon name={icon} size={20} /></span>
      <div>
        <div className="aii-sec-title" {...(helpText ? { 'data-help': helpText, 'data-help-title': title } : {})}>{title}{tm && <span className="po-tm">{tm}</span>}</div>
        {sub && <div className="aii-sec-sub">{sub}</div>}
      </div>
    </div>
    {right}
  </div>;
}

const poTaxAvatar = () => (window.__resources && window.__resources.superPlummaHero) || 'src/img/SuperPlumma.png';
function poProbMeta(probability) {
  if (probability >= 70) return { cls: 'hi', label: probability >= 90 ? t('pkn.tax.prob.veryLikely') : t('pkn.tax.prob.likely') };
  if (probability >= 50) return { cls: 'mid', label: t('pkn.tax.prob.possible') };
  return { cls: 'lo', label: t('pkn.tax.prob.uncertain') };
}

// ---------------- SuperPlumma-oppsummering (hero) ----------------
function PoTaxHero({ actions = [], estimate = null, onAsk, ruleVersion = '', taxYear }) {
  return <div className="aii-hero po-tax-hero">
    <div className="aii-hero-grid">
      <div>
        <div className="aii-hero-tag"><Icon name="sparkle" size={16} />{t('pkn.tax.heroTag', { year: taxYear })}</div>
        <div className="aii-hero-lead">{estimate === null
          ? <React.Fragment>{t('pkn.tax.leadNoEstimate', { year: taxYear })}</React.Fragment>
          : <React.Fragment>{t('pkn.tax.leadEstimateA', { year: taxYear })} <b>{fmt(estimate)}</b>, {t('pkn.tax.leadEstimateB')}</React.Fragment>}</div>
        <div className="t-xs" style={{ color: 'rgba(255,255,255,.78)', marginTop: 8 }}>{t('pkn.tax.heroMeta', { year: taxYear, rules: ruleVersion || t('pkn.tax.unversioned') })}</div>
        <div className="aii-hero-foot">
          <span className="aii-hero-pill"><Icon name="receipt" size={15} />{actions.length ? t('pkn.tax.actionsFound', { count: actions.length }) : t('pkn.tax.noActionsFound')}</span>
          <span className="aii-hero-pill"><Icon name="calendar" size={15} />{t('pkn.tax.returnDeadline')}</span>
          {onAsk && <button className="aii-hero-pill aii-hero-pill-btn" onClick={onAsk}><Icon name="sparkle" size={15} />{t('po.askAboutTax')}</button>}
        </div>
        {actions.length > 0 && <div className="po-hero-acts">
          {actions.map((action, index) => <div key={action.title} className="po-hero-act">
            <div className="po-hero-act-top">
              <span className="po-hero-act-rank">{index + 1}</span>
              <span className="po-hero-act-ico"><Icon name={action.icon} size={17} /></span>
              <span className={`po-hero-act-tag${action.urgent ? ' urgent' : ''}`}>{action.urgent && <Icon name="clock" size={12} />}{action.tag}</span>
            </div>
            <div className="po-hero-act-title">{action.title}</div>
            <div className="po-hero-act-val">{action.value}</div>
          </div>)}
        </div>}
      </div>
      <button type="button" className="aii-plum-launch" onClick={onAsk} title={t('aii.chatWithSuperPlumma')}>
        <img className="aii-hero-plum" src={poTaxAvatar()} alt="SuperPlumma" />
        <span className="aii-plum-hint"><Icon name="sparkle" size={13} />{t('aii.chatWithMe')}</span>
      </button>
    </div>
  </div>;
}

// ---------------- Skattemeldingsassistent (wizard) ----------------
const PO_SITUASJON = [
  { icon: 'user', key: 'enslig', label: 'Enslig' },
  { icon: 'users', key: 'gift', label: 'Gift' },
  { icon: 'users', key: 'samboer', label: 'Samboer' },
  { icon: 'users', key: 'barn', label: 'Barn' },
  { icon: 'fileText', key: 'student', label: 'Student' },
  { icon: 'clock', key: 'pensjonist', label: 'Pensjonist' },
  { icon: 'briefcase', key: 'naering', label: 'Selvstendig næringsdrivende' },
];
const PO_STEP_ICONS = { Bolig: 'home', 'Bolig og gjeld': 'home', Familie: 'users', Fradrag: 'receipt', Inntekt: 'wallet', Næring: 'briefcase', Oppsummering: 'checkCircle', 'Din situasjon': 'users' };

function PoTaxWizard({ initialReviewed = false, initialSituations = [], onSave, rowsByStep = {} }) {
  const [situation, setSituation] = poTaxUseState(() => new Set(initialSituations));
  const [step, setStep] = poTaxUseState(0);
  React.useEffect(() => { setSituation(new Set(initialSituations)); }, [initialSituations]);
  const toggle = (key) => setSituation((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key);
    else {
      if (key === 'enslig' || key === 'gift' || key === 'samboer') { next.delete('enslig'); next.delete('gift'); next.delete('samboer'); }
      next.add(key);
    }
    onSave?.({ reviewed: false, situations: [...next] });
    return next;
  });
  const steps = poTaxUseMemo(() => {
    const list = ['Din situasjon', 'Inntekt', 'Bolig og gjeld'];
    if (situation.has('barn') || situation.has('gift') || situation.has('samboer')) list.push('Familie');
    if (situation.has('naering')) list.push('Næring');
    list.push('Fradrag', 'Oppsummering');
    return list;
  }, [situation]);
  const current = Math.min(step, steps.length - 1);
  const label = steps[current];
  const rows = rowsByStep[label] || [];

  const content = label === 'Din situasjon'
    ? <div className="po-wiz-rows">
      <div className="t-sm muted" style={{ fontWeight: 600, lineHeight: 1.5 }}>Velg det som passer din situasjon, så tilpasser SuperPlumma spørsmålene og henter inn riktige skjemaer.</div>
      <div className="po-wiz-chips" style={{ marginTop: 6 }}>
        {PO_SITUASJON.map((option) => <button key={option.key} className={`po-wiz-chip${situation.has(option.key) ? ' on' : ''}`} onClick={() => toggle(option.key)}>
          <span className="po-wiz-tick">{situation.has(option.key) && <Icon name="check" size={12} stroke={3} />}</span>{option.label}
        </button>)}
      </div>
    </div>
    : <div className="po-wiz-rows">
      {rows.length === 0
        ? <div className="t-sm muted" style={{ fontWeight: 600, lineHeight: 1.5 }}>Vi har ikke hentet inn opplysninger for «{label}» ennå. Når skattegrunnlaget er koblet på, fyller SuperPlumma ut tallene her automatisk fra arbeidsgiver, bank og folkeregisteret.</div>
        : rows.map((row) => <div key={row.label} className="po-wiz-row"><span className="l">{row.label}</span><span className="v">{row.value}</span></div>)}
    </div>;

  return <Card>
    <PoSecHead icon="fileText" title={t('po.taxAssistant')} sub={t('po.taxAssistantSub')} />
    <div style={{ marginTop: 16, overflowX: 'auto' }}><Stepper steps={steps} current={current} onJump={(index) => setStep(index)} /></div>
    <div className="po-wiz-step">
      <div className="po-wiz-step-h">
        <span className="po-wiz-step-ico"><Icon name={PO_STEP_ICONS[label] || 'fileText'} size={18} /></span>
        <div><div className="po-wiz-step-title">{label}</div><div className="aii-sec-sub">Steg {current + 1} {t('sharedText.transaction.by')} {steps.length}</div></div>
      </div>
      {content}
    </div>
    <div className="po-wiz-foot">
      <Button variant="ghost" icon="chevLeft" disabled={current === 0} onClick={() => setStep(current - 1)}>{t('cust.prev')}</Button>
      <div className="po-wiz-dots">{steps.map((_, index) => <span key={index} className={`po-wiz-dot${index === current ? ' on' : ''}`} />)}</div>
      {current < steps.length - 1
        ? <Button iconRight="chevRight" onClick={() => setStep(current + 1)}>{t('cust.next')}</Button>
        : <Button icon="check" onClick={() => { onSave?.({ reviewed: true, situations: [...situation] }); toast(initialReviewed ? 'Skattesjekklisten er oppdatert' : 'Skattesjekklisten er gjennomgått', 'success'); }} data-help={t('helpTopics.taxEstimate.body')} data-help-title={t('helpTopics.taxEstimate.title')}>{t('bank.finishReview')}</Button>}
    </div>
  </Card>;
}

// ---------------- Fradragsjeger ----------------
function PoDeductionFinder({ deductions = [] }) {
  const [open, setOpen] = poTaxUseState(() => new Set());
  const ranked = poTaxUseMemo(() => deductions
    .map((deduction) => ({ ...deduction, score: Math.round((deduction.benefit || 0) * deduction.probability / 100) }))
    .sort((a, b) => b.score - a.score), [deductions]);
  const total = ranked.reduce((sum, deduction) => sum + deduction.benefit, 0);
  const toggle = (id) => setOpen((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  return <Card>
    <PoSecHead icon="sparkle" title={t('po.deductionHunter')} tm="™" sub={t('po.deductionHunterSub')} />
    {ranked.length === 0
      ? <div className="gj-empty" style={{ marginTop: 14 }}>{t('pkn.tax.noDeductions')}</div>
      : <React.Fragment>
        <div className="aii-summary" style={{ background: 'var(--tint-green)', borderColor: 'var(--success-bd)', marginTop: 14 }}>
          <span className="aii-summary-ico" style={{ background: 'var(--success)' }}><Icon name="sparkle" size={18} /></span>
          <p style={{ color: 'var(--ink)' }}>{total > 0 ? <b>{t('pkn.tax.provisionalEffect', { amount: fmt(total) })} </b> : <b>{t('pkn.tax.checkpointsFound', { count: ranked.length })} </b>}{t('pkn.tax.onlyCandidatesNote')}</p>
        </div>
        <div className="po-ded-list" style={{ marginTop: 16 }}>
          {ranked.map((deduction, index) => {
            const meta = poProbMeta(deduction.probability);
            const isOpen = open.has(deduction.id);
            return <div key={deduction.id} className={`po-ded${isOpen ? ' open' : ''}`}>
              <div className="po-ded-rank">{index + 1}</div>
              <span className="po-ded-ico"><Icon name={deduction.icon} size={19} /></span>
              <div className="po-ded-main">
                <div className="po-ded-head">
                  <span className="po-ded-name">{deduction.name}</span>
                  <span className={`po-ded-status s-${meta.cls}`}>{deduction.status}</span>
                </div>
                <div className="po-ded-desc">{deduction.description}</div>
                <div className="po-ded-prob">
                  <span className="po-ded-prob-lbl">{t('po.probability')}</span>
                  <div className="po-ded-prob-track"><div className={`po-ded-prob-fill f-${meta.cls}`} style={{ width: `${deduction.probability}%` }} /></div>
                  <span className={`po-ded-prob-pct p-${meta.cls}`}>{deduction.probability}% · {meta.label}</span>
                </div>
                <button className="po-ded-why-btn" onClick={() => toggle(deduction.id)} aria-expanded={isOpen}>
                  <Icon name="sparkle" size={14} />{t('po.whyIThinkThis')}<Icon name={isOpen ? 'chevUp' : 'chevDown'} size={15} />
                </button>
                {isOpen && <div className="po-ded-why">
                  <div className="po-ded-why-h">{t('po.dataIUsed')}</div>
                  <div className="po-ded-why-data">
                    {(deduction.evidence || []).map((item, key) => <span key={key} className="po-ded-chip"><Icon name="check" size={12} stroke={3} />{item}</span>)}
                  </div>
                  {deduction.transactions?.length > 0 && <details className="po-ded-transactions"><summary>{t('pkn.tax.showSignalTx')}</summary>{deduction.transactions.map((entry, key) => <div key={`${entry.date}-${entry.description}-${key}`}><span>{entry.date || t('pkn.tax.unknownDate')} · {entry.description}</span><b>{fmt(entry.amount)}</b></div>)}</details>}
                  <div className="po-ded-why-h" style={{ marginTop: 14 }}>{t('po.whyYouQualify')}</div>
                  <p className="po-ded-why-reason">{deduction.reason}</p>
                  {deduction.missing?.length > 0 && <React.Fragment><div className="po-ded-why-h" style={{ marginTop: 14 }}>{t('pkn.tax.missingHeading')}</div><div className="po-ded-why-data">{deduction.missing.map((item) => <span key={item} className="po-ded-chip"><Icon name="clock" size={12} />{item}</span>)}</div></React.Fragment>}
                  {deduction.sourceUrl && <a className="po-ded-source" href={deduction.sourceUrl} target="_blank" rel="noreferrer">{t('pkn.tax.readRule')} <Icon name="arrowUpRight" size={13} /></a>}
                </div>}
              </div>
              <div className="po-ded-benefit">
                <div className="lbl">{deduction.benefit == null ? t('pkn.tax.statusLabel') : t('po.estimatedTaxBenefit')}</div>
                <div className={`val${deduction.benefit == null ? ' pending' : ''}`}>{deduction.benefit == null ? t('pkn.tax.mustClarify') : fmt(deduction.benefit)}</div>
              </div>
            </div>;
          })}
        </div>
      </React.Fragment>}
  </Card>;
}

// ---------------- Skatteoptimalisering ----------------
function PoTaxOpt({ optimizations = [], taxYear }) {
  return <Card>
    <PoSecHead icon="percent" title={t('po.taxOptimisation')} sub={t('po.taxOptimisationSub')} />
    {optimizations.length === 0
      ? <div className="gj-empty" style={{ marginTop: 14 }}>{t('pkn.tax.noOptimisations')}</div>
      : <React.Fragment>
        <div className="po-opt-grid" style={{ marginTop: 14 }}>
          {optimizations.map((item) => <div key={item.name} className="po-opt">
            <div className="po-opt-top">
              <span className="po-opt-ico"><Icon name={item.icon} size={18} /></span>
              <span className="po-opt-name">{item.name}</span>
              <span className={`po-opt-frist${item.soon ? ' soon' : ''}`}><Icon name="calendar" size={13} />{item.deadline}</span>
            </div>
            <div className="po-opt-desc">{item.description}</div>
            <div className="po-opt-effects">
              <div className="po-opt-eff">
                <div className="lbl">{t('pkn.tax.effectThisYear', { year: taxYear })}</div>
                <div className="val neg">{item.now}</div>
              </div>
              <div className="po-opt-arrow"><Icon name="arrowRight" size={16} /></div>
              <div className="po-opt-eff">
                <div className="lbl">{t('po.nextYearOnwards')}</div>
                <div className={`val${String(item.next || '').startsWith('−') ? ' neg' : ' mut'}`}>{item.next}</div>
                <div className="sub">{item.nextNote}</div>
              </div>
            </div>
          </div>)}
        </div>
      </React.Fragment>}
    <div className="aii-disclaimer">
      <Icon name="info" size={17} />
      <p>{t('pkn.tax.disclaimer')}</p>
    </div>
  </Card>;
}

// ---------------- AI Skattekontroll ----------------
function PoTaxReview({ checks = [], confidence = null }) {
  return <Card>
    <PoSecHead icon="checkCircle" title={t('po.aiTaxCheck')} sub={t('po.aiTaxCheckSub')}
      right={confidence === null ? null : <span className="po-review-conf"><Icon name="checkCircle" size={14} />{t('sharedText.po.checkBasis')} {confidence} %</span>} />
    {checks.length === 0
      ? <div className="gj-empty" style={{ marginTop: 14 }}>{t('pkn.tax.reviewEmpty')}</div>
      : <div className="aii-risk-grid" style={{ marginTop: 14 }}>
        {checks.map((check) => <div key={check.name} className={`aii-risk aii-risk-l-${check.level}`}>
          <span className="aii-risk-ico"><Icon name={check.icon} size={20} /></span>
          <div style={{ minWidth: 0 }}>
            <div className="aii-risk-head">
              <span className="aii-risk-name">{check.name}</span>
              <span className="aii-risk-lvl">{check.level === 'hoy' ? t('pkn.tax.level.missing') : check.level === 'middels' ? t('pkn.tax.level.check') : t('pkn.tax.level.ok')}</span>
            </div>
            <div className="aii-risk-desc">{check.description}</div>
          </div>
        </div>)}
      </div>}
  </Card>;
}

// ---------------- Klar for innsending ----------------
// Norwegian strings are ids matched against the backend `completed` list; display goes through PO_CHECKLIST_KEYS.
const PO_CHECKLIST = ['Inntekt gjennomgått', 'Gjeld gjennomgått', 'Formue gjennomgått', 'Investeringer gjennomgått', 'Fradrag gjennomgått', 'Familieopplysninger gjennomgått'];
const PO_CHECKLIST_KEYS = { 'Inntekt gjennomgått': 'income', 'Gjeld gjennomgått': 'debt', 'Formue gjennomgått': 'wealth', 'Investeringer gjennomgått': 'investments', 'Fradrag gjennomgått': 'deductions', 'Familieopplysninger gjennomgått': 'family' };

function PoReadyToFile({ completed = [], missing = [], onGo, score = 0 }) {
  const [openSection, setOpenSection] = poTaxUseState(true);
  const done = new Set(completed);
  const total = Math.max(PO_CHECKLIST.length, completed.length + missing.length);
  const remainingCount = missing.length;
  return <Card>
    <PoSecHead icon="send" title={t('po.readyToFile')} sub={t('po.readyToFileSub')} />
    <div className="po-file" style={{ marginTop: 14 }}>
      <div className="po-file-list">
        {PO_CHECKLIST.map((item) => <div key={item} className="po-file-item" style={done.has(item) ? null : { opacity: 0.55 }}>
          <span className="po-file-check" style={done.has(item) ? null : { background: 'var(--panel-2)', color: 'var(--muted-2)' }}><Icon name={done.has(item) ? 'check' : 'clock'} size={15} stroke={3} /></span>
          <span className="po-file-label">{t('pkn.tax.checklist.' + PO_CHECKLIST_KEYS[item])}</span>
        </div>)}
      </div>
      <div className="po-file-side">
        <span className="po-file-conf-lbl">{t('sharedText.po.checkBasis')}</span>
        <span className="po-file-conf-val">{score} %</span>
        <span className="po-file-conf-sub">{remainingCount} {t('sharedText.transaction.by')} {total} {t('pkn.tax.pointsRemain')} · {100 - score} %</span>
        <Button className="po-file-cta" icon="lock" disabled title={t('po.requiresIntegration')} data-help={t('helpTopics.taxEstimate.body')} data-help-title={t('helpTopics.taxEstimate.title')}>{t('po.filingUnavailable')}</Button>
      </div>
    </div>

    <div className="po-sec">
      <button className="po-sec-h" onClick={() => setOpenSection((value) => !value)} aria-expanded={openSection}>
        <span className="po-sec-h-ico"><Icon name="lock" size={16} /></span>
        <span className="po-sec-h-title">{t('po.whatIsMissing')}</span>
        <span className="po-sec-h-gap">{remainingCount} {t('sharedText.transaction.by')} {total} {t('pkn.tax.pointsLeft')} · {100 - score} %</span>
        <Icon name={openSection ? 'chevUp' : 'chevDown'} size={17} />
      </button>
      <div className="po-sec-bar" title={t('pkn.tax.qualityAssured', { score })}>
        <div className="po-sec-bar-fill" style={{ width: `${score}%` }} />
        <div className="po-sec-bar-gap" style={{ width: `${100 - score}%` }} />
      </div>
      {openSection && <div className="po-sec-body">
        <div className="po-sec-col">
          <div className="po-sec-col-h po-sec-col-h-miss"><Icon name="alertCircle" size={15} />{t('po.whatRemains')}</div>
          {missing.length === 0
            ? <div className="po-sec-miss"><div><div className="po-sec-miss-title">{t('po.taxBasisNotFetched')}</div><div className="po-sec-miss-action">{t('pkn.tax.missingWhenConnected')}</div></div></div>
            : missing.map((item, index) => <div key={index} className="po-sec-miss">
              <span className="po-sec-pts">{index + 1}</span>
              <div>
                <div className="po-sec-miss-title">{item.title}</div>
                <div className="po-sec-miss-action">{item.action}</div>
                {item.go && onGo && <button className="po-sec-miss-go" type="button" onClick={() => onGo(item.go, item.goTarget || (item.go === 'skatt' ? 'po-tax-wizard-target' : null))}><Icon name="chevRight" size={14} />{item.goLabel || t('pkn.tax.goThere')}</button>}
              </div>
            </div>)}
        </div>
        <div className="po-sec-col">
          <div className="po-sec-col-h po-sec-col-h-done"><Icon name="checkCircle" size={15} />{t('po.alreadyVerified')}</div>
          <div className="po-sec-done-list">
            {completed.length === 0
              ? <div className="po-sec-done" style={{ opacity: 0.7 }}>{t('po.nothingVerified')}</div>
              : completed.map((item, index) => <div key={index} className="po-sec-done">
                <span className="po-sec-done-tick"><Icon name="check" size={12} stroke={3} /></span>{item}
              </div>)}
          </div>
        </div>
      </div>}
    </div>
  </Card>;
}

Object.assign(window, { PoDeductionFinder, PoReadyToFile, PoSecHead, PoTaxHero, PoTaxOpt, PoTaxReview, PoTaxWizard });
